import { useState } from "react";
import { Link } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nosible/shared/components/ui/table";
import { Badge } from "@nosible/shared/components/ui/badge";
import { Button } from "@nosible/shared/components/ui/button";
import { trpc } from "@/admin/lib/trpc";
import { ChevronLeft, ChevronRight, CreditCard, PackagePlus, Users } from "lucide-react";

export function AdminPlans() {
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const {
    data: plansData,
    isLoading,
    error,
  } = trpc.admin.listPlans.useQuery(
    {
      limit: pageSize,
      offset: page * pageSize,
    },
    {
      enabled: true,
    }
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-red-400">Error loading plans: {error.message}</div>
      </div>
    );
  }

  const totalPages = Math.ceil((plansData?.total ?? 0) / pageSize);

  const formatPrice = (cents: number) => {
    if (cents === 0) return "Free";
    return `$${(cents / 100).toFixed(2)}/mo`;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <CreditCard className="h-6 w-6 text-violet-pulse" />
          <h1 className="text-2xl font-bold text-silica font-display">Products</h1>
        </div>
        <Button className="brand-primary-button" size="sm" asChild>
          <Link to="/plans/create">
            <PackagePlus className="h-4 w-4 mr-2" />
            Create product
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Products</CardTitle>
          <CardDescription>
            Manage commercial access products and their rate-limit templates.
            Total: {plansData?.total ?? 0} products
          </CardDescription>
        </CardHeader>
        <CardContent>
          {plansData?.plans.length === 0 ? (
            <div className="text-center py-8 text-chromium-mist">
              <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No products found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>API Keys</TableHead>
                  <TableHead>Subscriptions</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plansData?.plans.map((plan: NonNullable<typeof plansData>["plans"][number]) => (
                  <TableRow key={plan.id}>
                    <TableCell className="font-medium text-silica">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-chromium-mist" />
                        {plan.name}
                      </div>
                      {plan.description && (
                        <p className="text-xs text-chromium-mist mt-1">{plan.description}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-chromium-mist">
                      {formatPrice(plan.monthlyPriceCents)}
                    </TableCell>
                    <TableCell className="text-chromium-mist">
                      {plan.defaultApiKeyLimit} max
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="flex items-center gap-1 w-fit">
                        <Users className="h-3 w-3" />
                        {plan.subscriptionCount}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {plan.active ? (
                        <Badge variant="outline" className="text-neon-green border-neon-green">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                      >
                        <Link to={`/plans/${plan.id}`}>
                          View
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-core">
              <div className="text-sm text-chromium-mist">
                Page {page + 1} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
